<script>
    import { onMount, tick, untrack } from "svelte";
    import { fade } from "svelte/transition";
    import { format, locale } from "svelte-i18n";
    import { params } from "@roxi/routify";
    import * as bootstrap from "bootstrap";
    import dayjs from "dayjs";
    import relativeTime from "dayjs/plugin/relativeTime";
    import localizedFormat from "dayjs/plugin/localizedFormat";
    import "dayjs/locale/es";
    import "dayjs/locale/en";

    import findingService from "../../finding/v2-service.js";
    import AssetsApi from "../../asset/assets.api.js";
    import userService from "../../user/service.svelte.js";
    import evidenceService from "../../../services/evidence.js";
    import vulnerabilityService from "../../vulnerability/service.js";
    import BreadCrumNav from "../../../components/breadcrum-nav.svelte";
    import FindingDetail from "./_components/finding-detail.svelte";
    import Properties from "../../../components/properties.svelte";
    import VulnerabilityDetail from "../../vulnerability/_components/vulnerability-detail.svelte";
    import AssetDetail from "../../asset/[kind]/[id]/_components/asset-detail.svelte";
    import InlineEdit from "../../../components/inline-edit.svelte";
    import AddPropsForm from "../_add_props_form.svelte";
    import DeletePropsForm from "../_delete_props_form.svelte";
    import Evidences from "../_components/evidences.svelte";
    import Gallery from "../_components/gallery.svelte";
    import Spinner from "../../../components/spinner.svelte";
    import logService from "../../log/service.js";
    import LogList from "../../../components/list.svelte";
    import tagService from "../../tag/service.js";
    import Tag from "../../../components/tags.svelte";
    import { user } from "../../user/user.state.svelte.js";
    import PropNameRow from "../../../components/powerTable/prop-name-row.svelte";
    import PropValueRow from "../../../components/powerTable/prop-value-row.svelte";
    import PropLocRow from "../../../components/powerTable/prop-loc-row.svelte";
    import PropKindRow from "../../../components/powerTable/prop-kind-row.svelte";
    import TagForm from "../../tag/_form.svelte";

    dayjs.locale($locale);
    dayjs.extend(relativeTime);
    dayjs.extend(localizedFormat);

    let { id: propId = null } = $props();
    let id = $derived(propId || $params?.id);

    let asset = $state({});
    let storedAsset = $state({});
    let properties = $state([]);
    let propsResponse = $state([]);
    let ungroupedProperties = $state([]);
    let img_src = $state("");
    let current_evidence = $state({});
    let evidenceUrls = $state({});
    let reload = $state(false);
    let reload_props = $state(false);
    let reload_tags = $state(false);
    let is_visible = $state(true);
    let userCanAdd = $state(false);
    let finding = $state(null);
    let evidences = $state([]);
    let is_loading = $state(false);
    let showProperties = $state(true);
    let showSettings = $state(false);
    let props_grouped_by = $state("none");
    let showVulnerabilityInfo = $state(true);
    let loadLogData = $state(false);
    let formData = $state({});
    let tags = $state([]);
    let allTags = $state([]);
    let showTagsForm = $state(false);
    let showTagsDeleteForm = $state(false);
    let refresh_tag_comp = $state(false);
    let is_loading_tags = $state(false);
    let lang = $state("es");
    let isDropdownOpen = $state(false);

    let galleryModal;
    let formEditModal;
    let addPropsModal;
    let deletePropsModal;
    let addEvidenceModal;
    let tagModal;
    let logModal;

    let propInstructs = $state([
        {
            key: "kind",
            title: $format("list.columns.kind"),
            filterable: false,
            parseAs: "component",
            dataComponent: PropKindRow,
        },
        {
            key: "name",
            title: $format("list.columns.name"),
            filterable: false,
            parseAs: "component",
            dataComponent: PropNameRow,
        },
        {
            key: "value",
            title: $format("list.columns.value"),
            filterable: false,
            parseAs: "component",
            dataComponent: PropValueRow,
        },
        {
            key: "location",
            title: $format("list.columns.location"),
            filterable: false,
            parseAs: "component",
            dataComponent: PropLocRow,
        },
        {
            key: "last_discovery_at",
            title: $format("list.columns.seen"),
            filterable: false,
            parseAs: "unsafe-html",
        },
    ]);

    const clickOutside = (node, callback) => {
        const handleClick = (event) => {
            if (
                node &&
                !node.contains(event.target) &&
                !event.defaultPrevented
            ) {
                callback();
            }
        };
        document.addEventListener("click", handleClick, true);
        return {
            destroy() {
                document.removeEventListener("click", handleClick, true);
            },
        };
    };

    const getTagsAssociation = async () => {
        return await tagService.search("v2/tag/association/", {});
    };

    const getAllTags = async (config = { filter: {} }) => {
        allTags = await tagService.search("v2/tag/", config);
        return allTags;
    };

    const getTags = async () => {
        if (!id) return { tags: [], associations: [], allTags: [] };
        is_loading_tags = true;
        let associations = await getTagsAssociation();
        allTags = await getAllTags();
        let ids = [];
        for (let association of associations) {
            if (
                association.target_object_type === "Finding" &&
                association.target_object_id === id
            ) {
                ids = [...ids, association.tag_id];
            }
        }
        tags = allTags.filter((tag) => ids.includes(tag.id));
        is_loading_tags = false;
        return { tags, associations, allTags };
    };

    const closeTagModal = () => {
        if (tagModal) tagModal.hide();
    };

    const addNewTag = (event) => {
        closeTagModal();
        return handleTagEdit({
            detail: event.detail.tag
                ? event.detail.tag
                : event.detail.savedTag.id,
        });
    };

    const handleTagEdit = (event) => {
        formData.tag = event.detail;
        if (!formData.tag || formData.tag === "cancel" || !id) return;
        let reqData = {
            tag_id: formData.tag,
            target_object_type: "Finding",
            target_object_id: id,
        };
        tagService
            .saveAssociation(reqData, [], {})
            .catch(() => false)
            .then(async () => {
                refresh_tag_comp = !refresh_tag_comp;
                showTagsForm = false;
                await getTags();
            });
    };

    const handleTagDelete = (event) => {
        formData.tag = event.detail;
        if (!formData.tag || formData.tag === "cancel" || !id) return;
        let reqData = {
            tag_id: formData.tag.tag,
            target_object_type: "Finding",
            target_object_id: id,
        };
        tagService
            .deleteAssociation(reqData, [], {})
            .catch(() => false)
            .then(async () => {
                refresh_tag_comp = !refresh_tag_comp;
                showTagsForm = false;
                showTagsDeleteForm = false;
                await getTags();
            });
    };

    const handleTagsReset = () => {
        showTagsForm = false;
        showTagsDeleteForm = false;
    };

    const getAssetData = async (assetId) => {
        if (!assetId) return {};
        let assetRes = await AssetsApi.findAsset(assetId);
        storedAsset = assetRes;
        return assetRes;
    };

    const getFindingEvidences = async (findingId) => {
        if (!findingId) return [];
        let evs = await evidenceService.search();
        return evs.filter((ev) => ev.finding_id === findingId);
    };

    const getFindingVulnerability = async (vulnId) => {
        if (!vulnId) return {};
        return await vulnerabilityService.find("v2/vulnerability", vulnId);
    };

    const getProperties = async (config = { filter: {} }) => {
        let props = await findingService.search("v2/finding-property/", config);
        if (props.length > 0) showProperties = true;
        return props;
    };

    const setPropsState = async () => {
        if (!id) return [];
        is_loading = true;
        propsResponse = await getProperties({ filter: { finding_id: id } });
        for (let prop of propsResponse) {
            if (prop.location === "") prop.location = "General";
        }
        ungroupedProperties = JSON.parse(JSON.stringify(propsResponse));
        properties = propsResponse;
        is_loading = false;
        return properties;
    };

    const groupPropertiesByKNV = (props) => {
        if (props_grouped_by === "knv") return;
        is_loading = true;
        let propsGrouped = props.reduce((a, b) => {
            const found = a.find(
                (e) =>
                    e.name === b.name &&
                    e.value === b.value &&
                    e.kind === b.kind,
            );
            if (found) {
                found.location = found.location + "," + b.location;
            } else {
                a.push({ ...b, location: b.location });
            }
            return a;
        }, []);

        properties = propsGrouped;
        props_grouped_by = "knv";
        propInstructs = propInstructs.filter(
            (col) => col.key !== "last_discovery_at",
        );
        is_loading = false;
        showSettings = false;
    };

    const ungroupProperties = () => {
        if (props_grouped_by === "none") return;
        is_loading = true;
        properties = propsResponse;
        props_grouped_by = "none";
        propInstructs = [
            ...propInstructs,
            {
                key: "last_discovery_at",
                title: "Vista",
                filterable: false,
                parseAs: "unsafe-html",
            },
        ];
        is_loading = false;
        showSettings = false;
    };

    const getEvidenceContent = async (evId) => {
        if (!evId) return "";
        if (evidenceUrls[evId]) return evidenceUrls[evId];
        const file = await evidenceService.getContent(evId);
        const fileBlob = await file.blob();
        const fileUrl = URL.createObjectURL(fileBlob);
        evidenceUrls[evId] = fileUrl;
        return fileUrl;
    };

    const handleDeleteEvidence = (evidence) => {
        evidenceService
            .del(evidence)
            .then(() => handleTableRefresh())
            .catch((err) => err);
    };

    const handleEvidenceEditFn = async (event, evidenceItem) => {
        let newEvidence = !evidenceItem
            ? event.detail
            : { id: evidenceItem.id, description: event.detail.value };
        evidenceService
            .save(newEvidence)
            .then(() => {
                handleTableRefresh();
                if (galleryModal) galleryModal.hide();
            })
            .catch(() => false);
    };

    const showGallery = (src, evidenceItem) => {
        img_src = src;
        current_evidence = evidenceItem;
        if (galleryModal) galleryModal.show();
    };

    const showNextGalleryItem = (event) => {
        is_visible = false;
        if (!finding?.evidence) return;
        let idx = finding.evidence.findIndex(
            (fev) => fev.id === current_evidence.id,
        );

        if (event.detail === "forward") {
            current_evidence =
                idx === finding.evidence.length - 1
                    ? finding.evidence[0]
                    : finding.evidence[idx + 1];
        } else if (event.detail === "back") {
            current_evidence =
                idx === 0
                    ? finding.evidence[finding.evidence.length - 1]
                    : finding.evidence[idx - 1];
        }

        img_src = evidenceUrls[current_evidence.id] || "";
        reload = !reload;
        is_visible = true;
    };

    const loadFindingData = async (findingId) => {
        if (!findingId) return null;
        let pFinding = await findingService.find("v2/finding", findingId, {});

        let pAsset = await getAssetData(pFinding.asset_id);
        pFinding.asset = pAsset;
        asset = pAsset;

        let evs = await getFindingEvidences(findingId);
        pFinding.evidence = evs.sort((a, b) =>
            a.description
                ? b.description
                    ? a.description.localeCompare(b.description)
                    : -1
                : 1,
        );

        pFinding.vulnerability = await getFindingVulnerability(
            pFinding.vulnerability_id,
        );

        await getTags();

        finding = pFinding;
        return finding;
    };

    const handleTableRefresh = async () => {
        if (!id) return;
        is_loading = true;
        if (addEvidenceModal) addEvidenceModal.hide();
        evidenceUrls = {};
        await loadFindingData(id);
        await tick();
        is_loading = false;
    };

    const openLogModal = async () => {
        loadLogData = true;
        await tick();
        const bs =
            typeof bootstrap !== "undefined" ? bootstrap : window.bootstrap;
        if (bs) {
            const logModalEl = document.getElementById("get-log-modal");
            if (logModalEl) {
                if (!logModal) {
                    logModal = new bs.Modal(logModalEl);
                    logModalEl.addEventListener("hidden.bs.modal", () => {
                        loadLogData = false;
                        logModal = null;
                    });
                }
                logModal.show();
            }
        }
    };

    const getLog = async () => {
        if (!id) return [];
        let logs = await logService.search({ filter: { search: id } });
        logs.forEach((log) => {
            let timestamp = dayjs(log.timestamp).format().split("T");
            let time = timestamp[1].split("-");
            log.date = dayjs(timestamp[0]).format("YYYY-MM-DD");
            log.time = time[0];
            if (log.target_object_name && log.target_object_name.length > 40) {
                log.long_target_object_name = log.target_object_name.split(",");
            }
        });
        return parseLogsFunc(logs);
    };

    const closeAddPropsModal = () => {
        if (addPropsModal) addPropsModal.hide();
    };

    const closeDeletePropsModal = () => {
        if (deletePropsModal) deletePropsModal.hide();
    };

    // This is ONLY called when the user successfully saves properties
    const handlePropsSaved = () => {
        setPropsState();
        closeAddPropsModal();
        closeDeletePropsModal();
    };

    const changeLanguage = (currLang) => {
        lang = currLang === "es" ? "en" : "es";
    };

    const toggleCollapsibleItems = (clicked) => {
        if (clicked === "properties") {
            showProperties = !showProperties;
            showSettings = showProperties ? showSettings : false;
        } else {
            showSettings = !showSettings;
            showProperties = showSettings ? true : showProperties;
        }
    };

    let inlineEdit_evidenceDesc = {
        type: "text",
        placeholder: "Agregar descripción",
        lang: { edit: "Editar", save: "Guardar", cancel: "Cancelar" },
    };

    const translateEventId = (value) => {
        if ($locale !== "es" || !value) return value || "";
        const labels = {
            finding: "Hallazgo",
            service: "Servicio",
            certificate: "Certificado",
            product: "Producto",
            user: "Usuario",
            network: "Red",
            domain: "Dominio",
            cloud: "Cuenta cloud",
            ip: "IP",
            Document: "Documento",
            document: "Documento",
            "asset.public_ip": "IP",
            "asset.private_ip": "IP",
            "asset.public_network": "Red",
            "asset.private_network": "Red",
            "asset.finding": "Hallazgo",
            "asset.certificate": "Certificado",
            "asset.domain": "Dominio",
            "asset.ad": "Active Directory",
            "asset.software": "Código fuente",
            "asset.endpoint": "Endpoint",
            "asset.other": "Otros activos",
            "asset.cloud": "Cuenta Cloud",
            "asset.hostname": "Hostname",
        };

        let regex = new RegExp(value, "gi");
        if (value.includes("token.create")) value = "Inicio de sesión";
        else if (value.includes("token.delete")) value = "Cierre de sesión";
        else if (value.includes("password"))
            value = value.replace("password", "Contraseña");
        else if (value.includes("otac")) value = value.replace("otac", "OTAC");
        else if (value.includes(".create")) value = "Creación";
        else if (value.includes(".delete")) value = "Eliminación";
        else if (value.includes("document.processed"))
            value = "Documento procesado";
        else if (value.includes("finding.open")) value = "Hallazgo abierto";
        else if (value.includes("update")) value = "Actualización";
        else if (value.includes("Property")) value = "Propiedad";
        else if (value.match(regex))
            value = value.replace(regex, labels[value.toLowerCase()] || value);

        return value === "undefined" ? "" : value;
    };

    const logColumnsDef = [
        { key: "date", title: $format("list.columns.date"), filterable: false },
        { key: "time", title: $format("list.columns.hour"), filterable: false },
        {
            key: "target_object_type",
            title: $format("list.columns.type"),
            filterable: false,
        },
        {
            key: "source_user_name",
            title: $format("list.columns.user"),
            filterable: false,
        },
        {
            key: "event_id",
            title: $format("list.columns.event"),
            filterable: false,
        },
        {
            key: "target_object_name",
            title: $format("list.columns.data"),
            filterable: false,
        },
        {
            key: "event_data",
            title: $format("list.columns.resource"),
            filterable: false,
        },
    ];

    onMount(async () => {
        await tick();
        const bs =
            typeof bootstrap !== "undefined" ? bootstrap : window.bootstrap;
        if (bs) {
            const elGallery = document.getElementById("gallery-modal");
            if (elGallery) galleryModal = new bs.Modal(elGallery);

            const elEdit = document.getElementById("finding-edit-modal");
            if (elEdit) formEditModal = new bs.Modal(elEdit);

            const elAddProps = document.getElementById("finding-add-props-modal");
            if (elAddProps) {
                addPropsModal = new bs.Modal(elAddProps);
                elAddProps.addEventListener("hidden.bs.modal", () => reload_props = !reload_props);
            }

            const elDelProps = document.getElementById("finding-delete-props-modal");
            if (elDelProps) {
                deletePropsModal = new bs.Modal(elDelProps);
                elDelProps.addEventListener("hidden.bs.modal", () => reload_props = !reload_props);
            }

            const elAddEv = document.getElementById("add-evidence-modal");
            if (elAddEv) addEvidenceModal = new bs.Modal(elAddEv);

            const elTag = document.getElementById("tag-add-new-modal");
            if (elTag) {
                tagModal = new bs.Modal(elTag);
                elTag.addEventListener("hidden.bs.modal", () => reload_tags = !reload_tags);
            }
        }
        userCanAdd = await userService.can("finding", "add");
    });

    $effect(() => {
        if (id) {
            untrack(() => {
                loadFindingData(id);
                setPropsState();
            });
        }
    });
</script>

<style>
    .breadcrumb {
        padding: 0 15px !important;
    }
    .modal.show .modal-dialog {
        max-width: 1080px;
        text-align: left !important;
    }
    .evidences-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
        gap: 1.5rem;
        margin-top: 1rem;
    }
    .evidence-card {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        min-height: 240px;
        background: #f8f9fa;
        padding: 10px;
        border-radius: 8px;
        border: 1px solid #e9ecef;
    }
    .gallery-container.is-visible {
        opacity: 1;
    }
    .gallery-container {
        opacity: 0;
        transition: all 0.5s ease;
    }
    .btn-collapse {
        border: none;
        background-color: transparent;
    }
    .log-btn {
        height: 27px !important;
        width: auto !important;
        max-width: 100px !important;
        font-size: 0.75em;
        padding: 0 5px !important;
    }
    .dropdown-toggle:after {
        content: none !important;
    }
    .btn-secondary.dropdown-toggle,
    .btn-secondary:not(:disabled):not(.disabled):active,
    .btn-secondary:not(:disabled):not(.disabled).active,
    .show > .btn-secondary.dropdown-toggle {
        background-color: rgba(248, 249, 250, 0.85) !important;
    }
    .is-selected {
        background-color: #f8f9fa !important;
        color: #0056b3 !important;
    }
</style>

<section class="content-header">
    <div class="container-fluid">
        <div class="row">
            <div class="col-12">
                <h1 class="mt-3 mb-3 page-title">
                    {#if finding && finding.vulnerability}
                        {#if finding.vulnerability.level !== "good" && finding.vulnerability.level !== "info"}
                            <span
                                class="cvss-badge cvss-{finding.vulnerability
                                    .level || 'none'}"
                                style="margin-right: .5em;"
                            >
                                {findingService.labels[
                                    finding.vulnerability.level
                                ] || ""}
                            </span>
                        {/if}
                        {#if $locale === "es" && lang === "es"}
                            {finding.vulnerability.title_es ||
                                finding.vulnerability.title} - {finding.asset
                                ?.nickname ||
                                finding.asset?.name ||
                                ""}
                        {:else}
                            {finding.vulnerability.title ||
                                finding.vulnerability.id} - {finding.asset
                                ?.nickname ||
                                finding.asset?.name ||
                                ""}
                        {/if}
                    {/if}
                </h1>
            </div>
            <div class="col-12 mt-3 breadcrumb">
                <BreadCrumNav
                    routes={[{ path: "/finding", label: "Hallazgos" }]}
                />
            </div>
        </div>
    </div>
</section>

<section class="content">
    <div class="container-fluid">
        <div class="row">
            <div class="col-12 d-flex" style="gap: 15px;">
                <div class="card mt-3" style="flex: 2;">
                    <!-- CRITICAL FIX: Safe structural check ensures all required children exist before mounting detail component -->
                    {#if finding && finding.vulnerability && finding.asset}
                        <div class="card-header">
                            <div class="card-title">{$format("data")}</div>
                            <div class="card-tools">
                                <button
                                    title="Ver logs"
                                    class="btn btn-secondary table-button log-btn"
                                    onclick={openLogModal}
                                >
                                    <i class="fas fa-align-justify"></i>
                                    {$format("see_log")}
                                </button>
                            </div>
                        </div>
                        <FindingDetail {finding} />
                    {:else}
                        <div class="card-header">
                            <div class="card-title">Datos</div>
                        </div>
                        <div class="card-body">
                            <div class="overlay">
                                <i class="fas fa-2x fa-sync-alt fa-spin"></i>
                            </div>
                        </div>
                    {/if}
                </div>
                <div class="card mt-3" style="flex: 1;">
                    {#if finding && finding.asset}
                        <div class="card-header">
                            <div class="card-title">
                                {$format("page.finding.info_of_asset")}
                            </div>
                        </div>
                        <div class="card-body">
                            <AssetDetail
                                asset={storedAsset}
                                navigation={true}
                            />
                        </div>
                    {:else}
                        <div class="card-header">
                            <div class="card-title">
                                {$format("page.finding.info_of_asset")}
                            </div>
                        </div>
                        <div class="card-body">
                            <div class="overlay">
                                <i class="fas fa-2x fa-sync-alt fa-spin"></i>
                            </div>
                        </div>
                    {/if}
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-12">
                <div class="card mt-3">
                    <div class="card-header">
                        <div class="card-title">
                            {$format("page.finding.finding_tags")}
                        </div>
                        {#if user.data.role !== "viewer"}
                            <div class="card-tools">
                                {#if !showTagsForm && !showTagsDeleteForm}
                                    <div
                                        class="dropdown d-inline-block"
                                        use:clickOutside={() =>
                                            (isDropdownOpen = false)}
                                    >
                                        <button
                                            type="button"
                                            title="{$format('add')} {$format(
                                                'tags',
                                            )}"
                                            aria-label={$format("buttons.add")}
                                            class="btn btn-secondary table-button btn-add btn-xs dropdown-toggle"
                                            onclick={() =>
                                                (isDropdownOpen =
                                                    !isDropdownOpen)}
                                            aria-expanded={isDropdownOpen}
                                        >
                                            <i class="fas fa-plus"></i>
                                        </button>
                                        <ul
                                            class="dropdown-menu dropdown-menu-end"
                                            class:show={isDropdownOpen}
                                            style="position: absolute; right: 0; left: auto; margin-top: 2px;"
                                        >
                                            <li>
                                                <a
                                                    class="dropdown-item"
                                                    href="#"
                                                    onclick={(e) => {
                                                        e.preventDefault();
                                                        isDropdownOpen = false;
                                                        showTagsForm = true;
                                                    }}
                                                    >{$format(
                                                        "buttons.select_tag.new",
                                                    )}</a
                                                >
                                            </li>
                                            <li>
                                                <a
                                                    class="dropdown-item"
                                                    href="#"
                                                    onclick={(e) => {
                                                        e.preventDefault();
                                                        isDropdownOpen = false;
                                                        if (tagModal)
                                                            tagModal.show();
                                                    }}
                                                    >{$format(
                                                        "buttons.select_tag.add_and_associate",
                                                    )}</a
                                                >
                                            </li>
                                        </ul>
                                    </div>
                                    <button
                                        title="{$format('remove')} {$format(
                                            'tags',
                                        )}"
                                        aria-label="{$format(
                                            'remove',
                                        )} {$format('tags')}"
                                        class="btn btn-secondary table-button btn-delete btn-xs"
                                        onclick={() =>
                                            (showTagsDeleteForm = true)}
                                    >
                                        <i class="fas fa-trash"></i>
                                    </button>
                                {:else}
                                    <button
                                        title={$format("back")}
                                        aria-label={$format("back")}
                                        class="btn btn-xs"
                                        onclick={() => handleTagsReset()}
                                    >
                                        <i class="fas fa-backward"></i>
                                    </button>
                                {/if}
                            </div>
                        {/if}
                    </div>
                    <div class="card-body">
                        {#key refresh_tag_comp}
                            {#if is_loading_tags}
                                <Spinner />
                            {:else}
                                <Tag
                                    {tags}
                                    {allTags}
                                    {showTagsForm}
                                    {showTagsDeleteForm}
                                    on:submitTags={addNewTag}
                                    onsubmitTags={addNewTag}
                                    on:deleteTags={handleTagDelete}
                                    ondeleteTags={handleTagDelete}
                                    on:cancel={handleTagsReset}
                                    oncancel={handleTagsReset}
                                />
                            {/if}
                        {/key}
                    </div>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-12">
                <div class="card mt-3">
                    <div class="card-header">
                        <div class="card-title">{$format("properties")}</div>
                        <div class="float-end">
                            <button
                                class="btn btn-default btn-collapse float-start"
                                type="button"
                                onclick={(e) => {
                                    e.preventDefault();
                                    toggleCollapsibleItems("settings");
                                }}
                            >
                                <i class="fas fa-cog"></i>
                            </button>
                            {#if showSettings}
                                <div
                                    class="float-start"
                                    transition:fade={{ delay: 0, duration: 50 }}
                                >
                                    <div
                                        class="dropdown d-inline-block"
                                        use:clickOutside={() =>
                                            (showSettings = false)}
                                    >
                                        <ul
                                            class="dropdown-menu dropdown-menu-xl dropdown-menu-right show"
                                            style="left: inherit; right: 0px; position: absolute; margin-top: 2px; display: block;"
                                        >
                                            <li>
                                                <a
                                                    href="#"
                                                    class="dropdown-item"
                                                    class:is-selected={props_grouped_by ===
                                                        "knv"}
                                                    onclick={(e) => {
                                                        e.preventDefault();
                                                        groupPropertiesByKNV(
                                                            properties,
                                                        );
                                                    }}
                                                >
                                                    <i
                                                        class="fas fa-object-group me-1"
                                                    ></i>Agrupar por tipo,
                                                    nombre y valor
                                                </a>
                                            </li>
                                            <li>
                                                <div
                                                    class="dropdown-divider"
                                                ></div>
                                            </li>
                                            <li>
                                                <a
                                                    href="#"
                                                    class="dropdown-item"
                                                    class:is-selected={props_grouped_by ===
                                                        "none"}
                                                    onclick={(e) => {
                                                        e.preventDefault();
                                                        ungroupProperties();
                                                    }}
                                                >
                                                    <i
                                                        class="fas fa-object-ungroup me-1"
                                                    ></i>Desagrupar
                                                </a>
                                            </li>
                                            {#if user.data.is_internal}
                                                <li>
                                                    <div
                                                        class="dropdown-divider"
                                                    ></div>
                                                </li>
                                                <li>
                                                    <button
                                                        class="dropdown-item mt-3 border-0 bg-transparent w-100 text-start"
                                                        type="button"
                                                        title="Agregar propiedades"
                                                        onclick={() => {
                                                            showSettings = false;
                                                            if (addPropsModal)
                                                                addPropsModal.show();
                                                        }}
                                                    >
                                                        <i class="fa fa-plus"
                                                        ></i>
                                                        {$format("add")}
                                                        {$format("properties")}
                                                    </button>
                                                </li>
                                                {#if properties.length > 0}
                                                    <li>
                                                        <div
                                                            class="dropdown-divider"
                                                        ></div>
                                                    </li>
                                                    <li>
                                                        <button
                                                            class="dropdown-item border-0 bg-transparent w-100 text-start"
                                                            type="button"
                                                            title="Eliminar propiedades"
                                                            onclick={() => {
                                                                showSettings = false;
                                                                if (
                                                                    deletePropsModal
                                                                )
                                                                    deletePropsModal.show();
                                                            }}
                                                        >
                                                            <i
                                                                class="fa fa-times"
                                                            ></i> Eliminar propiedades
                                                        </button>
                                                    </li>
                                                {/if}
                                            {/if}
                                        </ul>
                                    </div>
                                </div>
                            {/if}
                            <button
                                class="btn btn-default btn-collapse float-end"
                                type="button"
                                onclick={() =>
                                    toggleCollapsibleItems("properties")}
                            >
                                <i
                                    class={!showProperties
                                        ? "fas fa-angle-down"
                                        : "fas fa-angle-up"}
                                ></i>
                            </button>
                        </div>
                    </div>
                    {#if showProperties}
                        <div
                            class="card-body"
                            transition:fade|global={{ delay: 0, duration: 200 }}
                        >
                            <div class="table-responsive table-readOnly">
                                {#if !is_loading}
                                    <Properties
                                        {properties}
                                        ptInstructs={propInstructs}
                                    />
                                {:else}
                                    <div class="overlay text-center">
                                        <i class="fas fa-2x fa-sync-alt fa-spin"
                                        ></i>
                                    </div>
                                {/if}
                            </div>
                        </div>
                    {/if}
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-12">
                <div class="card mt-3">
                    <div class="card-header">
                        <div class="card-title">
                            {$format("page.finding.info_of_vul")}
                        </div>
                        <button
                            class="btn btn-default btn-collapse float-end"
                            type="button"
                            onclick={() =>
                                (showVulnerabilityInfo =
                                    !showVulnerabilityInfo)}
                        >
                            <i
                                class={!showVulnerabilityInfo
                                    ? "fas fa-angle-down"
                                    : "fas fa-angle-up"}
                            ></i>
                        </button>
                    </div>
                    {#if showVulnerabilityInfo}
                        <div
                            class="card-body"
                            transition:fade|global={{ delay: 0, duration: 50 }}
                        >
                            <!-- CRITICAL FIX: Explicit check for vulnerability before mounting detail component -->
                            {#if finding && finding.vulnerability}
                                <VulnerabilityDetail
                                    vulnerability={finding.vulnerability}
                                    navigation={true}
                                    on:changeLanguage={() =>
                                        changeLanguage(lang)}
                                    onchangeLanguage={() =>
                                        changeLanguage(lang)}
                                />
                            {:else}
                                <div class="overlay">
                                    <i
                                        class="fas fa-2x fa-sync-alt fa-spin mt-5"
                                    ></i>
                                </div>
                            {/if}
                        </div>
                    {/if}
                </div>

                <div class="card mt-3" style="margin-left: 0;">
                    <div class="card-header">
                        <div class="card-title">
                            {$format("evidences")}
                            {#if user.data.is_internal}
                                <button
                                    class="btn-add btn-add-evidences btn btn-xs"
                                    onclick={() =>
                                        addEvidenceModal &&
                                        addEvidenceModal.show()}
                                    style="position: absolute; right: 15px;"
                                >
                                    <i class="fas fa-plus"></i>
                                </button>
                            {/if}
                        </div>
                    </div>
                    <div class="card-body mt-3 mb-5">
                        {#if !is_loading}
                            {#if finding?.evidence && finding.evidence.length > 0}
                                <div class="evidences-grid">
                                    {#each finding.evidence as evidence}
                                        <div class="evidence-card text-center">
                                            <div>
                                                {#if user.data.is_internal}
                                                    <InlineEdit
                                                        {...inlineEdit_evidenceDesc}
                                                        value={evidence.description}
                                                        on:save={(e) =>
                                                            handleEvidenceEditFn(
                                                                e,
                                                                evidence,
                                                            )}
                                                        onsave={(e) =>
                                                            handleEvidenceEditFn(
                                                                e,
                                                                evidence,
                                                            )}
                                                        on:cancel={() =>
                                                            (evidence.description =
                                                                evidence.description)}
                                                        oncancel={() =>
                                                            (evidence.description =
                                                                evidence.description)}
                                                    />
                                                {:else}
                                                    <div class="fw-bold mb-2">
                                                        {evidence.description ||
                                                            ""}
                                                    </div>
                                                {/if}
                                            </div>

                                            <div
                                                class="my-2 d-flex justify-content-center align-items-center"
                                                style="min-height: 140px;"
                                            >
                                                {#await getEvidenceContent(evidence.id)}
                                                    <i
                                                        class="fas fa-2x fa-sync-alt fa-spin"
                                                    ></i>
                                                {:then route}
                                                    <button
                                                        class="btn p-0 border-0"
                                                        onclick={() =>
                                                            showGallery(
                                                                route,
                                                                evidence,
                                                            )}
                                                    >
                                                        <img
                                                            style="max-width: 100%; max-height: 140px; border-radius: 6px; object-fit: cover;"
                                                            src={route}
                                                            alt="Evidence thumbnail"
                                                        />
                                                    </button>
                                                {/await}
                                            </div>

                                            {#if user.data.is_internal}
                                                <div>
                                                    <button
                                                        class="btn btn-sm btn-outline-danger w-100 mt-2"
                                                        onclick={(e) => {
                                                            e.preventDefault();
                                                            handleDeleteEvidence(
                                                                evidence,
                                                            );
                                                        }}
                                                    >
                                                        {$format(
                                                            "buttons.delete",
                                                        )}
                                                    </button>
                                                </div>
                                            {/if}
                                        </div>
                                    {/each}
                                </div>
                            {:else}
                                <div style="font-size: 1.25em;">
                                    {$format("results_not_found")}
                                </div>
                                {#if user.data.is_internal}
                                    <div class="mt-3">
                                        <button
                                            class="btn-add btn-add-evidences btn btn-primary"
                                            onclick={() =>
                                                addEvidenceModal &&
                                                addEvidenceModal.show()}
                                            >{$format("buttons.add")}</button
                                        >
                                    </div>
                                {/if}
                            {/if}
                        {:else}
                            <div class="overlay">
                                <i class="fas fa-2x fa-sync-alt fa-spin"></i>
                            </div>
                        {/if}
                    </div>
                </div>
            </div>
        </div>
    </div>
</section>

<!-- MODALS -->
<div class="modal fade" id="tag-add-new-modal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">{$format("page.tag.add")}</h4>
                <button
                    type="button"
                    class="btn-close"
                    aria-label="Close"
                    onclick={closeTagModal}
                ></button>
            </div>
            <div class="modal-body">
                {#key reload_tags}
                    <TagForm
                        on:save={addNewTag}
                        onsave={addNewTag}
                        on:closeModal={closeTagModal}
                        oncloseModal={closeTagModal}
                        tag={null}
                    />
                {/key}
            </div>
        </div>
    </div>
</div>

<div class="modal fade" id="gallery-modal" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog">
        <div class="modal-content">
            <div class="modal-header" style="border: none;">
                <h2>
                    Evidencia {current_evidence.description
                        ? "- " + current_evidence.description
                        : ""}
                </h2>
                <button
                    type="button"
                    class="btn-close"
                    aria-label="Close"
                    onclick={() => galleryModal && galleryModal.hide()}
                ></button>
            </div>
            <div class="modal-body" style="padding: 4em;">
                {#key reload}
                    <div
                        class="gallery-container"
                        class:is-visible={is_visible}
                    >
                        <Gallery
                            {img_src}
                            evidence={current_evidence}
                            on:edit={handleEvidenceEditFn}
                            onedit={handleEvidenceEditFn}
                            on:pageChange={showNextGalleryItem}
                            onpageChange={showNextGalleryItem}
                        />
                    </div>
                {/key}
            </div>
        </div>
    </div>
</div>

<div
    class="modal fade"
    id="add-evidence-modal"
    tabindex="-1"
    aria-hidden="true"
>
    <div class="modal-dialog">
        <div class="modal-content">
            <div class="modal-header" style="border: none;">
                <h4>{$format("add")}</h4>
                <button
                    type="button"
                    class="btn-close"
                    aria-label="Close"
                    onclick={() => addEvidenceModal && addEvidenceModal.hide()}
                ></button>
            </div>
            <div class="modal-body">
                <Evidences
                    finding_id={id}
                    on:dataRefresh={handleTableRefresh}
                    ondataRefresh={handleTableRefresh}
                    on:files_uploaded={handleTableRefresh}
                    onfiles_uploaded={handleTableRefresh}
                />
            </div>
        </div>
    </div>
</div>

{#if loadLogData}
    <div
        class="modal fade"
        id="get-log-modal"
        tabindex="-1"
        aria-hidden="true"
    >
        <div class="modal-dialog" style="max-width: 80%; width: 80%;">
            <div class="modal-content">
                <div class="modal-header">
                    <h4 class="modal-title">Logs</h4>
                    <button
                        type="button"
                        class="btn-close"
                        aria-label="Close"
                        onclick={() => logModal && logModal.hide()}
                    ></button>
                </div>
                <div class="modal-body">
                    {#await getLog()}
                        <div class="card mt-3">
                            <div class="card-body"><Spinner /></div>
                        </div>
                    {:then log}
                        {#if !is_loading}
                            <LogList
                                data={log}
                                instructs={logColumnsDef}
                                parseFunc={parseLogsFunc}
                                prefix={"logsTable"}
                            />
                        {/if}
                    {/await}
                </div>
            </div>
        </div>
    </div>
{/if}

<div
    class="modal fade"
    id="finding-add-props-modal"
    tabindex="-1"
    aria-hidden="true"
>
    <div class="modal-dialog">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">{$format("add")} Propiedades</h4>
                <!-- X button only closes -->
                <button
                    type="button"
                    class="btn-close"
                    aria-label="Close"
                    onclick={closeAddPropsModal}
                ></button>
            </div>
            <div class="modal-body">
                {#if finding}
                    {#key reload_props}
                        <AddPropsForm
                            finding_id={id}
                            customProps={[{}]}
                            on:saveProp={handlePropsSaved}
                            onsaveProp={handlePropsSaved}
                            on:cancel={closeAddPropsModal}
                            oncancel={closeAddPropsModal}
                        />
                    {/key}
                {/if}
            </div>
        </div>
    </div>
</div>

<div
    class="modal fade"
    id="finding-delete-props-modal"
    tabindex="-1"
    aria-hidden="true"
>
    <div class="modal-dialog">
        <div class="modal-content">
            <div class="modal-header">
                <h4 class="modal-title">
                    {$format("buttons.delete")} Propiedades de Hallazgo
                </h4>
                <!-- X button only closes -->
                <button
                    type="button"
                    class="btn-close"
                    aria-label="Close"
                    onclick={closeDeletePropsModal}
                ></button>
            </div>
            <div class="modal-body">
                {#if finding}
                    {#key reload_props}
                        <DeletePropsForm
                            properties={ungroupedProperties}
                            on:saveProp={handlePropsSaved}
                            onsaveProp={handlePropsSaved}
                            on:cancel={closeDeletePropsModal}
                            oncancel={closeDeletePropsModal}
                        />
                    {/key}
                {/if}
            </div>
        </div>
    </div>
</div>